// apps/web/src/lib/portal-acessos.ts — Fase 17, Plano 13 (PORT-01; D-04, D-09, D-10, D-11; PORT-UI-20..23).
//
// Chamadas à API INTERNA de gestão de acessos do portal (/api/v1/portal-acessos; cookie interno + CSRF interno,
// permissão `portal:gerenciar-acessos`) e regras puras da tela "Acessos do portal": rótulos, ações da linha,
// validação do convite e tradução de erros. O valor do link gerado só existe na resposta que o cria: este módulo
// nunca o guarda (nem em Web Storage, nem em URL, nem em log).
import type {
  AcessoPortalLista,
  AcessoPortalView,
  CriarAcessoPortalInput,
  CriarAcessoPortalResposta,
  FiltroSituacaoAcessoPortal,
  GerarLinkPortalResposta,
  ClienteLookupPv,
  ReativarAcessoPortalResposta,
  SituacaoAcessoPortal,
} from '@pieltec/contracts';
import { apiFetch, apiMutate } from './csrf-fetch';

const BASE = '/api/v1/portal-acessos';
const FUSO = 'America/Sao_Paulo';
const TRACO = '—';

export const TAMANHO_PAGINA_ACESSOS = 25;

export const MENSAGEM_ERRO_CARGA = 'Não foi possível carregar os usuários do portal. Tente novamente.';
export const MENSAGEM_SEM_PERMISSAO_TELA = 'Você não tem permissão para gerenciar os acessos do portal.';
export const MENSAGEM_SEM_PERMISSAO_ACAO = 'Seu perfil não tem permissão para esta ação.';
export const MENSAGEM_EMAIL_DUPLICADO = 'Já existe um usuário do portal com este e-mail.';
export const MENSAGEM_VERSAO = 'Este usuário foi alterado por outra pessoa. Recarregue a lista e tente de novo.';
export const MENSAGEM_FALHA_ACAO = 'Não foi possível concluir a ação. Tente novamente.';
export const MENSAGEM_NOME_OBRIGATORIO = 'Informe o nome do usuário.';
export const MENSAGEM_EMAIL_INVALIDO = 'Informe um e-mail válido.';

// ─── Rótulos ────────────────────────────────────────────────────────────────────────────────────

/** Instante ISO para `dd/MM/yyyy HH:mm` em America/Sao_Paulo; texto inválido volta como veio. */
export function formatarDataHoraAcesso(iso: string): string {
  const data = new Date(iso);
  if (Number.isNaN(data.getTime())) return iso;
  const formato = new Intl.DateTimeFormat('pt-BR', {
    timeZone: FUSO,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  });
  const partes: Record<string, string> = {};
  for (const parte of formato.formatToParts(data)) partes[parte.type] = parte.value;
  return `${partes.day}/${partes.month}/${partes.year} ${partes.hour}:${partes.minute}`;
}

export function rotuloSituacaoAcesso(situacao: SituacaoAcessoPortal): string {
  switch (situacao) {
    case 'PENDENTE_PRIMEIRO_ACESSO':
      return 'Pendente de primeiro acesso';
    case 'ATIVO':
      return 'Ativo';
    case 'DESATIVADO':
      return 'Desativado';
  }
}

export const ROTULO_FILTRO_SITUACAO: Readonly<Record<FiltroSituacaoAcessoPortal, string>> = {
  TODOS: 'Todos',
  ATIVOS: 'Ativos',
  PENDENTES: 'Pendentes de primeiro acesso',
  DESATIVADOS: 'Desativados',
};

export const FILTROS_SITUACAO: readonly FiltroSituacaoAcessoPortal[] = ['TODOS', 'ATIVOS', 'PENDENTES', 'DESATIVADOS'];

export function rotuloUltimoAcesso(ultimoAcessoEm: string | null): string {
  return ultimoAcessoEm === null ? 'Nunca acessou' : formatarDataHoraAcesso(ultimoAcessoEm);
}

type CampoLink = Pick<AcessoPortalView, 'linkTipo' | 'linkValidoAte'>;

/** Há link e ele ainda não venceu. */
export function linkVivo(acesso: CampoLink, agora: Date = new Date()): boolean {
  if (acesso.linkTipo === null || acesso.linkValidoAte === null) return false;
  return Date.parse(acesso.linkValidoAte) > agora.getTime();
}

/** Coluna "Link": validade do link vivo, "expirado" ou traço. */
export function rotuloLinkAcesso(acesso: CampoLink, agora: Date = new Date()): string {
  if (acesso.linkTipo === null || acesso.linkValidoAte === null) return TRACO;
  const convite = acesso.linkTipo === 'CONVITE';
  if (!linkVivo(acesso, agora)) return convite ? 'Convite expirado' : 'Redefinição expirada';
  return `${convite ? 'Convite válido' : 'Redefinição válida'} até ${formatarDataHoraAcesso(acesso.linkValidoAte)}`;
}

// ─── Ações da linha ─────────────────────────────────────────────────────────────────────────────

export type IdAcaoAcesso = 'gerar-link' | 'gerar-novo-link' | 'revogar-link' | 'desativar' | 'reativar';

export interface AcaoLinha {
  readonly id: IdAcaoAcesso;
  readonly rotulo: string;
  readonly destrutiva: boolean;
}

/** Quais ações a linha oferece (PORT-UI-21): desativado só reativa; sem link vivo gera; com link vivo gera novo e revoga. */
export function acoesDaLinha(acesso: AcessoPortalView, agora: Date = new Date()): readonly AcaoLinha[] {
  if (acesso.situacao === 'DESATIVADO') return [{ id: 'reativar', rotulo: 'Reativar acesso', destrutiva: false }];
  const vivo = linkVivo(acesso, agora);
  const acoes: AcaoLinha[] = [
    acesso.linkTipo === null
      ? { id: 'gerar-link', rotulo: 'Gerar link', destrutiva: false }
      : { id: 'gerar-novo-link', rotulo: 'Gerar novo link', destrutiva: false },
  ];
  if (vivo) acoes.push({ id: 'revogar-link', rotulo: 'Revogar link', destrutiva: true });
  acoes.push({ id: 'desativar', rotulo: 'Desativar acesso', destrutiva: true });
  return acoes;
}

export interface TextosConfirmacao {
  readonly titulo: string;
  readonly corpo: string;
  readonly cancelar: string;
  readonly confirmar: string;
  readonly destrutiva: boolean;
  readonly enviando: string;
}

export type AcaoConfirmavel = 'desativar' | 'reativar' | 'revogar-link' | 'gerar-novo-link';

/** Títulos, corpos e botões das confirmações (PORT-UI-22/23), com o que será registrado. */
export function textosConfirmacao(
  acao: AcaoConfirmavel,
  acesso: Pick<AcessoPortalView, 'nome' | 'email'>,
  clienteNome: string,
): TextosConfirmacao {
  const { nome, email } = acesso;
  switch (acao) {
    case 'desativar':
      return {
        titulo: `Desativar o acesso de ${nome}?`,
        corpo: `${nome} (${email}) perderá o acesso ao portal de ${clienteNome} agora, e todas as sessões abertas serão encerradas na hora. Links de acesso pendentes deixam de funcionar. A ação fica registrada com o seu nome e a data.`,
        cancelar: 'Manter acesso',
        confirmar: 'Desativar acesso',
        destrutiva: true,
        enviando: 'Desativando…',
      };
    case 'reativar':
      return {
        titulo: `Reativar o acesso de ${nome}?`,
        corpo: `${nome} poderá entrar de novo no portal de ${clienteNome}. Como a senha anterior pode estar comprometida, você precisará gerar um novo link para ele definir uma nova senha.`,
        cancelar: 'Cancelar',
        confirmar: 'Reativar e gerar link',
        destrutiva: false,
        enviando: 'Reativando…',
      };
    case 'revogar-link':
      return {
        titulo: `Revogar o link de ${nome}?`,
        corpo: 'O link de primeiro acesso ou redefinição deixará de funcionar agora. O usuário continua cadastrado.',
        cancelar: 'Manter link',
        confirmar: 'Revogar link',
        destrutiva: true,
        enviando: 'Revogando…',
      };
    case 'gerar-novo-link':
      return {
        titulo: 'Gerar novo link?',
        corpo: `O link anterior de ${nome} deixará de funcionar.`,
        cancelar: 'Cancelar',
        confirmar: 'Gerar novo link',
        destrutiva: false,
        enviando: 'Gerando…',
      };
  }
}

// ─── Validação do convite ───────────────────────────────────────────────────────────────────────

const EMAIL_VALIDO = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export interface ErrosConvite {
  readonly nome?: string;
  readonly email?: string;
}

export function validarConvite(nome: string, email: string): ErrosConvite {
  const erros: { nome?: string; email?: string } = {};
  if (nome.trim().length === 0) erros.nome = MENSAGEM_NOME_OBRIGATORIO;
  const emailAparado = email.trim();
  if (emailAparado.length === 0 || emailAparado.length > 254 || !EMAIL_VALIDO.test(emailAparado)) erros.email = MENSAGEM_EMAIL_INVALIDO;
  return erros;
}

// ─── Erros ──────────────────────────────────────────────────────────────────────────────────────

export type TipoErroAcesso = 'permissao' | 'email' | 'versao' | 'conflito' | 'falha';

export interface ErroAcessoTraduzido {
  readonly tipo: TipoErroAcesso;
  readonly mensagem: string;
  /** Há estado antigo na tela: a pessoa deve recarregar a lista (conflito de versão ou de estado). */
  readonly recarregar: boolean;
}

/** Erro HTTP (ou de rede, `status === null`) da API de acessos; `mensagemServidor` é o texto devolvido pelo servidor. */
export class ErroAcessoPortal extends Error {
  readonly status: number | null;
  readonly mensagemServidor: string;

  constructor(status: number | null, mensagemServidor: string) {
    super(mensagemServidor || (status === null ? 'Falha de rede.' : `Erro ${status}`));
    this.name = 'ErroAcessoPortal';
    this.status = status;
    this.mensagemServidor = mensagemServidor;
  }
}

/** Traduz status + mensagem do servidor para o texto do UI-SPEC. `contexto: 'tela'` vale para a listagem; `'acao'` para os botões. */
export function mensagemErroAcesso(
  status: number | null,
  mensagemServidor = '',
  contexto: 'tela' | 'acao' = 'acao',
): ErroAcessoTraduzido {
  if (status === 403) {
    return { tipo: 'permissao', mensagem: contexto === 'tela' ? MENSAGEM_SEM_PERMISSAO_TELA : MENSAGEM_SEM_PERMISSAO_ACAO, recarregar: false };
  }
  if (status === 409) {
    if (mensagemServidor.includes('Já existe um usuário')) return { tipo: 'email', mensagem: MENSAGEM_EMAIL_DUPLICADO, recarregar: false };
    if (mensagemServidor.includes('alterado por outra pessoa')) return { tipo: 'versao', mensagem: MENSAGEM_VERSAO, recarregar: true };
    return { tipo: 'conflito', mensagem: mensagemServidor.length > 0 ? mensagemServidor : MENSAGEM_VERSAO, recarregar: true };
  }
  if (status === 404 && mensagemServidor.length > 0) return { tipo: 'conflito', mensagem: mensagemServidor, recarregar: true };
  return { tipo: 'falha', mensagem: MENSAGEM_FALHA_ACAO, recarregar: false };
}

function extrairMensagem(corpo: string): string {
  try {
    const dado = JSON.parse(corpo) as { message?: unknown };
    if (typeof dado.message === 'string') return dado.message;
    if (Array.isArray(dado.message) && typeof dado.message[0] === 'string') return dado.message[0];
  } catch {
    // corpo que não é JSON: sem mensagem utilizável
  }
  return '';
}

/** Converte o erro do `apiFetch` (mensagem = corpo da resposta, JSON do Nest) em `ErroAcessoPortal`. */
function erroDoFetch(erro: unknown): ErroAcessoPortal {
  if (erro instanceof Error) {
    try {
      const dado = JSON.parse(erro.message) as { statusCode?: unknown; message?: unknown };
      if (typeof dado.statusCode === 'number') return new ErroAcessoPortal(dado.statusCode, extrairMensagem(erro.message));
    } catch {
      // mensagem que não é JSON: falha de rede ou resposta sem corpo estruturado
    }
  }
  return new ErroAcessoPortal(null, '');
}

async function enviar(caminho: string, corpo: unknown): Promise<Response> {
  let resposta: Response;
  try {
    resposta = await apiMutate(caminho, { method: 'POST', body: corpo });
  } catch {
    throw new ErroAcessoPortal(null, '');
  }
  if (!resposta.ok) throw new ErroAcessoPortal(resposta.status, extrairMensagem(await resposta.text().catch(() => '')));
  return resposta;
}

// ─── Chamadas ───────────────────────────────────────────────────────────────────────────────────

export interface FiltroListagemAcessos {
  readonly clienteId: string;
  readonly busca?: string;
  readonly situacao?: FiltroSituacaoAcessoPortal;
  readonly pagina?: number;
}

export function queryListagemAcessos(filtro: FiltroListagemAcessos): string {
  const params = new URLSearchParams();
  params.set('clienteId', filtro.clienteId);
  const busca = filtro.busca?.trim() ?? '';
  if (busca.length > 0) params.set('busca', busca);
  if (filtro.situacao !== undefined && filtro.situacao !== 'TODOS') params.set('situacao', filtro.situacao);
  params.set('pagina', String(filtro.pagina ?? 1));
  return params.toString();
}

export async function listarAcessos(filtro: FiltroListagemAcessos): Promise<AcessoPortalLista> {
  try {
    return await apiFetch<AcessoPortalLista>(`${BASE}?${queryListagemAcessos(filtro)}`);
  } catch (erro) {
    throw erroDoFetch(erro);
  }
}

export async function criarAcesso(input: CriarAcessoPortalInput): Promise<CriarAcessoPortalResposta> {
  const resposta = await enviar(BASE, input);
  return (await resposta.json()) as CriarAcessoPortalResposta;
}

export async function gerarLink(id: string, versao: number): Promise<GerarLinkPortalResposta> {
  const resposta = await enviar(`${BASE}/${encodeURIComponent(id)}/links`, { versao });
  return (await resposta.json()) as GerarLinkPortalResposta;
}

export async function desativarAcesso(id: string, versao: number): Promise<void> {
  await enviar(`${BASE}/${encodeURIComponent(id)}/desativar`, { versao });
}

export async function reativarAcesso(id: string, versao: number): Promise<ReativarAcessoPortalResposta> {
  const resposta = await enviar(`${BASE}/${encodeURIComponent(id)}/reativar`, { versao });
  return (await resposta.json()) as ReativarAcessoPortalResposta;
}

export async function revogarLink(id: string, versao: number): Promise<void> {
  await enviar(`${BASE}/${encodeURIComponent(id)}/links/revogar`, { versao });
}

/**
 * Cliente do atalho `?clienteId=` do cadastro: a tela só tem o id, e o seletor precisa do nome. Lê a lista do cadastro
 * (`cadastros:consultar`, a mesma tela de onde o atalho parte); sem ela, o seletor mostra o cliente sem nome e a lista
 * de usuários continua funcionando, pois a API valida o `clienteId`.
 */
export async function buscarClientePorId(id: string): Promise<ClienteLookupPv> {
  try {
    const resposta = await apiFetch<{ data: readonly { id: string; codigo?: string | null; nome: string }[] }>('/api/v1/cadastros/clientes?apenasAtivos=false');
    const encontrado = resposta.data.find((cliente) => cliente.id === id);
    if (encontrado !== undefined) return { id, codigo: encontrado.codigo ?? '', nome: encontrado.nome };
  } catch {
    // sem permissão de cadastro ou rede: cai no cliente sem nome
  }
  return { id, codigo: id.slice(0, 8), nome: 'Cliente selecionado' };
}
